import Cookie from 'js-cookie';
import api from './http';
import {
  TenantBranding,
  ContactType,
  IdentificationSessionPreferencesRes,
  QueryLegalPersonForIdentityCheckFlowRes,
  QueryLegalPersonRes,
  SendOtpRes,
  Session,
  VerifyOtpForIdentityCheckFlowRes,
  VerifyOtpRes,
} from './types';

/**
 * If there are any verified development for the current session, returns the
 * the link to the verified development customer portal along data for other
 * developments in which the legal person of the session owns a contract.
 * If there are no verified developments in the session, returns 204 (No Content)
 *
 * Only the developments served at the current host are returned.
 */
export async function getSession(): Promise<Session | null> {
  if (!Cookie.get('xcsrftoken')) return null;
  const { data, status } = await api.get('/api/v1/current-session', { host: window.location.host });
  if (status === 204) return null;
  return data;
}

/**
 * Clear the session cookies and mark the session as deleted in the database
 * The session cookies deleted are "sid" and "xcsrftoken"
 */
export async function deleteSession(): Promise<void> {
  await api.delete('/api/v1/current-session');
}

/**
 * When there is a legal persons with the given document in the database,
 * returns it along with the developments and contacts associated with it
 * and creates a new session for it. Two session cookies are created: one
 * http only session cookie called "sid" which stores the session id and
 * another non http only csrf token cookie called "xcsrftoken" which stores
 * the csrf token that should be sent in every subsequent request in the
 * X-Csrf-Token header
 *
 * When there are no matched customers, returns 204. Does not set any cookies.
 * If an ip address makes too many hourly requests to this function, returns 429.
 * This method is also rate limited per document to avoid brute force attacks.
 *
 * Only the developments served at the current host are returned.
 */
export async function queryLegalPerson(document: string): Promise<QueryLegalPersonRes> {
  const { data } = await api.get('/api/v1/legal-persons', { document, host: window.location.host });
  return data;
}

export async function sendOtp(
  document: string,
  developmentId: number,
  contactType: ContactType,
  contactId: number,
): Promise<SendOtpRes> {
  const { data } = await api.post(`/api/v1/send-otp`, { document, developmentId, contactType, contactId });
  return data;
}

/**
 * For a document whose developments queryLegalPerson did not return. Always
 * resolves with an otp id, whether or not the document has a development with a
 * contact of the given type: nothing tells whether the otp was sent.
 */
export async function sendOtpToContactType(contactType: ContactType): Promise<SendOtpRes> {
  const { data } = await api.post(`/api/v1/send-otp-to-contact-type`, { contactType });
  return data;
}

export async function resendOtp(otpId: string) {
  await api.post(`/api/v1/resend-otp`, { otpId });
}

/**
 * Each otp is valid for 5 minutes and 3 attempts.
 *
 * If rememberMe is true, save the development as verified in the session. The
 * rememberMe is per development, so a user may choose to remember one development
 * and not remember another. We don't clear the session if rememberMe is false,
 * to do so the user must click in the logout button
 *
 * @throws VerifyOtpError
 */
export async function verifyOtp(otpId: string, token: string, rememberMe: boolean): Promise<VerifyOtpRes> {
  const { data } = await api.post(`/api/v1/verify-otp`, { otpId, token, rememberMe });
  return data;
}

/**
 * Returns the preferences of the identification session created in the WhatsApp
 * channel. None of them is read anymore: the request is only used to tell
 * whether the session still exists.
 *
 * Returns null when the identification session is gone (204), either because
 * there is none with the given id or because it expired: an identification
 * session is only valid for 24h and the backend answers the same in both cases.
 */
export async function getIdentificationSessionPreferences(
  identificationSessionId: string,
): Promise<IdentificationSessionPreferencesRes | null> {
  const { data, status } = await api.get(`/api/v1/identity-check/session/${identificationSessionId}`);
  if (status === 204) return null;
  return data;
}

export async function queryLegalPersonForIdentityCheckFlow(
  document: string,
  identificationSessionId: string,
): Promise<QueryLegalPersonForIdentityCheckFlowRes> {
  const { data } = await api.get('/api/v1/identity-check/legal-persons', {
    document,
    identificationSessionId,
    host: window.location.host,
  });
  return data;
}

export async function sendOtpForIdentityCheckFlow(
  document: string,
  developmentId: number,
  contactType: ContactType,
  contactId: number,
  sessionId: string,
): Promise<SendOtpRes> {
  const { data } = await api.post(`/api/v1/identity-check/send-otp`, {
    document,
    developmentId,
    contactType,
    contactId,
    sessionId,
  });
  return data;
}

/** See sendOtpToContactType. */
export async function sendOtpToContactTypeForIdentityCheckFlow(
  contactType: ContactType,
  sessionId: string,
): Promise<SendOtpRes> {
  const { data } = await api.post(`/api/v1/identity-check/send-otp-to-contact-type`, { contactType, sessionId });
  return data;
}

export async function resendOtpForIdentityCheckFlow(otpId: string, sessionId: string) {
  await api.post(`/api/v1/identity-check/resend-otp`, { otpId, sessionId });
}

/**
 * Opting in for the notifications is no longer done from this flow, so the
 * backend is always told that the customer did not opt in, for any development.
 */
export async function verifyOtpForIdentityCheckFlow(
  otpId: string,
  token: string,
  sessionId: string,
  identificationSessionId: string,
): Promise<VerifyOtpForIdentityCheckFlowRes> {
  const { data } = await api.post(`/api/v1/identity-check/verify-otp`, {
    otpId,
    token,
    sessionId,
    identificationSessionId,
    isNotificationOptIn: false,
    additionalNotificationOptInDevelopmentIds: [],
  });
  return data;
}

/**
 * Resolves to null when the tenant served at this host has no custom branding.
 * Not-found is signalled as 204: the SPA's CloudFront distribution rewrites 404s
 * to index.html, so a 404 from the BFF would reach the client as a 200 carrying
 * HTML.
 */
export async function getBrandingForHost(host: string): Promise<TenantBranding | null> {
  const { status, data } = await api.get('/api/v1/branding', { host });
  if (status === 204 || !data) return null;
  return data;
}
